import { BuildingOfficeIcon, CalendarIcon, ShareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';
import { useState, useEffect, type JSX } from 'react';

import { cn, toDateFormat, useScroll } from '@restworld/utils-common';
import { Text, Alert } from '@restworld/ui-ds-base';

// import { useRecordJobShareEvent } from '../../hooks/use-record-job-share-event';
import { JobDetailProps } from './job-detail.mocks';
import { CompanyProfileDrawer } from '../job-company-profile';
import { jobPositionApiDataAtom, showViolaBadgeAtom } from '../job-position/job-position.atoms';
import { CardPrimary } from '../legacy/card';
import { JobShareModal } from '../legacy';

export const JobDetailBlock = ({
  jobPositionDetails,
  showRestaurant,
  children: applicationEntryPointButtonAsChildren,
  ambassadorRefCode,
  bgColor,
  showShareButton = true,
  jobInterestEl,
  hideJobApplicationEntryPoint = false,
  publishedAt,
  wentOfflineAt,
  performanceMetrics,
}: JobDetailProps): JSX.Element => {
  const { isScrolled } = useScroll();
  const showViolaBadge = useAtomValue(showViolaBadgeAtom);
  const jpData = useAtomValue(jobPositionApiDataAtom);
  const { t } = useTranslation('common');
  const { t: tJP } = useTranslation('job-position-page');

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const showAtsAlert = isMounted && jpData?.published_from_ats === true;

  return (
    <StickyJobDetailWrapper id="job-info-el" isScrolled={isScrolled} showRestaurant={showRestaurant} bgColor={bgColor}>
      <Wrapper>
        <CardPrimary
          jobPositionDetails={jobPositionDetails}
          showRestaurant={showRestaurant}
          showViolaBadge={showViolaBadge || false}
        />
        {!hideJobApplicationEntryPoint ? (
          showShareButton ? (
            <ButtonWrapper>
              {applicationEntryPointButtonAsChildren}
              <JobShareModal
                StartIcon={ShareIcon}
                buttonTitle=""
                ambassadorRefCode={ambassadorRefCode}
                jobPositionId={jobPositionDetails?.id}
                variant="secondary-reversed"
              />
            </ButtonWrapper>
          ) : (
            applicationEntryPointButtonAsChildren
          )
        ) : null}
        {showRestaurant && (
          <div className="hidden lg:block lg:w-full">
            <CompanyProfileDrawer buttonIcon={BuildingOfficeIcon} />
          </div>
        )}
        {jobInterestEl ? <div className="flex flex-row w-full items-start mt-2">{jobInterestEl}</div> : undefined}
        {hideJobApplicationEntryPoint && jobInterestEl && showShareButton ? (
          <JobShareModal
            StartIcon={ShareIcon}
            buttonTitle="Condividi"
            ambassadorRefCode={ambassadorRefCode}
            jobPositionId={jobPositionDetails?.id}
            variant="tertiary-reversed"
          />
        ) : null}
        {showAtsAlert && (
          <Alert
            text={<p>{tJP('alert_ats_description')}</p>}
            variant="restworld"
            className="w-full md:max-w-full md:p-4"
          />
        )}
      </Wrapper>
      {(publishedAt || wentOfflineAt || performanceMetrics) && (
        <FooterWrapper>
          {wentOfflineAt ? (
            <JobDetailParagraph icon={<CalendarIcon className="w-[13.5px] h-[13.5px] text-white" />}>{`${t(
              'suspended_on'
            )} ${toDateFormat(wentOfflineAt, `dd/MM/yyyy`)}`}</JobDetailParagraph>
          ) : publishedAt ? (
            <JobDetailParagraph icon={<CalendarIcon className="w-[13.5px] h-[13.5px] text-white" />}>{`${t(
              'published_on'
            )} ${toDateFormat(publishedAt, `dd/MM/yyyy`)}`}</JobDetailParagraph>
          ) : null}
        </FooterWrapper>
      )}
    </StickyJobDetailWrapper>
  );
};
export default JobDetailBlock;

export const StickyJobDetailWrapper = ({
  className,
  ...props
}: {
  isScrolled: boolean;
  showRestaurant: boolean;
  bgColor?: string;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const { bgColor = 'primary', showRestaurant, isScrolled: _isScrolled, ...rest } = props;
  const bgColorClass = { primary: 'bg-primary-800', secondary: 'bg-white', none: 'bg-transparent' }[bgColor] || '';
  return (
    <div
      className={cn(
        'rounded-[20px] w-full h-max lg:sticky lg:top-[6rem] lg:max-w-sm lg:min-w-[384px] px-6 pb-6 lg:shadow',
        bgColorClass,
        showRestaurant ? 'pt-16' : 'pt-6',
        className
      )}
      {...rest}
    />
  );
};

const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex gap-4 text-lg justify-center w-full min-w-full px-[0.5rem] lg:px-0', className)}
    {...props}
  />
);

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-6 items-center border-b border-primary-300 pb-4', className)} {...props} />
);
const FooterWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 items-center justify-start  w-full pt-4', className)} {...props} />
);

const JobDetailParagraph = ({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) => (
  <div className="flex flex-row gap-2 items-center justify-start w-fit">
    {icon}
    <Text size={2} color="white">
      {children}
    </Text>
  </div>
);
